import { useEffect, useState } from 'react';

import { Box, FullScreenLoader } from '@opendoor/bricks/core';

import { showPrompt } from 'src/helpers/showPrompt';
import { getPageSubmit } from 'src/lib/flow';
import { offerDashboardPath, sellerGlobalPagePath } from 'src/routes/pathBuilders';
import { useGetCustomerUuidQuery, useGetHumanQuery, useUpdateAnswersMutation } from 'src/store/api';
import { getFlowFromState } from 'src/store/flow_listeners';
import { useAppSelector } from 'src/store/hooks';

import { ReceptionLoader } from './ReceptionLoader';

const LoadOffer: React.FC = () => {
  const customerUuid = useAppSelector((state) => state.sellerInput.customerUUID);
  const sellerInputUuid = useAppSelector((state) => state.sellerInput.leadToken);
  const addressUuid: string = useAppSelector((state) => state.sellerInput.address.uuid);
  const { data: human } = useGetHumanQuery();
  const hasPendingMutations = useAppSelector((state) =>
    Object.values(state.gql.mutations).some((mutation) => mutation?.status === 'pending'),
  );
  const [updateAnswers, { data: slugUpdated }] = useUpdateAnswersMutation();
  const pageSetName = useAppSelector((state) => state.flow_ng.pageSetName);

  const { state: flowState, context: flowContext } = useAppSelector(getFlowFromState);

  const [timeout, setTimeout] = useState(false);
  // If we do not have a customerUuid, let's poll on getSellerInput and wait and see
  // if we get one
  useGetCustomerUuidQuery(sellerInputUuid, {
    pollingInterval: 500,
    skip: !!(!sellerInputUuid || customerUuid),
  });

  // Setup a 10sec timeout flag, we check this below and send the user to the
  // denials page on timeout
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setTimeout(true);
    }, 10000);
    return () => clearTimeout(timer);
  }, [setTimeout]);

  useEffect(() => {
    if (sellerInputUuid) {
      // if we have any inflight answers be sure to send them along with this request
      // this prevents us from losing any answers if an earlier request is slower than
      // our call below to update the slug
      const { submittedAnswers } = getPageSubmit(flowState, flowContext, flowContext.answers);

      // if we have prevented offer generation previously make sure at this point we kick it off
      submittedAnswers['seller.qualify_on_required_answers'] = true;

      updateAnswers({
        uuid: sellerInputUuid,
        completedSlug: '/p/last-step',
        submittedAnswers,
      });
    }
    // ignore any changes on flowState and flowContext, we only need the initial values to be sure we send any answers that are not yet confirmed as saved
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sellerInputUuid, updateAnswers]);

  useEffect(() => {
    // wait for any mutation to finish loading
    if (hasPendingMutations) {
      return;
    }
    if (!slugUpdated) {
      return;
    }

    // once we have a customer uuid we can now send the user to the offer dashboard
    if (customerUuid) {
      window.removeEventListener('beforeunload', showPrompt);
      const redirectParams = {};
      if (human?.opendoor_internal_access) {
        redirectParams['customer'] = customerUuid;
      }
      if (pageSetName === 'untreated_room_condition') {
        redirectParams['refresh_offer'] = 'true';
        redirectParams['refresh_source'] = 'home_details';
      }
      window.location.href = offerDashboardPath(addressUuid, redirectParams);
    }

    // TODO: There are times we will not get a customer uuid and it is because the user is denied, but
    // users can experience a timeout here because offer generation is slow, so show them the complete page
    // In the future we should figure out a stronger signal for the denied case and show them the right
    // denial page based on that.
    if (timeout) {
      window.removeEventListener('beforeunload', showPrompt);
      window.location.href = sellerGlobalPagePath({ token: sellerInputUuid, page: 'complete' });
    }
  }, [
    hasPendingMutations,
    customerUuid,
    timeout,
    addressUuid,
    sellerInputUuid,
    pageSetName,
    slugUpdated,
    human?.opendoor_internal_access,
  ]);

  // have the page display the full screen loader and hide any default loading
  // indicators we have in ReceptionLoader (but still load reception...)
  return (
    <Box height="100vh" width="100%">
      <FullScreenLoader />
      <Box display="none">
        <ReceptionLoader />
      </Box>
    </Box>
  );
};

export default LoadOffer;
